/**
 * CR-3396: ViewContext - Manages the current view context
 *
 * Database-backed view switching (like old system's SetCompanyDefault).
 * Persists view state to the backend, ensuring it survives across devices/sessions.
 *
 * Allows users to switch between:
 * - Group level view (aggregated data across all divisions in group)
 * - Division level view (data for specific division — "company" in DB/code)
 * - Site level view (data for specific site)
 * - Customer/Supplier/Customer Manager portal mode
 *
 * Customer Portal Mode:
 * - 'customer': User views as customer (managing suppliers) - default for customer companies
 * - 'supplier': User views as supplier (responding to customers) - default for supplier companies
 * - 'customer_manager': User views as supplier managing their customers - for Customer Portal Mode feature
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { coreAPI, Site, Company, CompanyGroup, ViewContextUpdate } from '../lib/api';
import { TokenManager } from '../lib/token-manager';

// View level types
export type ViewLevel = 'GROUP' | 'COMPANY' | 'SITE';
export type PortalMode = 'customer' | 'supplier' | 'customer_manager';

interface ViewState {
  // Current view level
  viewLevel: ViewLevel;

  // Selected entities
  selectedGroupId: number | null;
  selectedCompanyId: number | null;
  selectedSiteId: number | null;

  // Selected entity details (for display)
  selectedGroupName: string | null;
  selectedCompanyName: string | null;
  selectedSiteName: string | null;

  // Portal mode (customer or supplier view)
  portalMode: PortalMode;

  // Whether the user's company supports dual portal mode
  supportsDualMode: boolean;

  // Whether the user's company supports customer portal mode (suppliers managing customers)
  supportsCustomerPortal: boolean;

  // Security: If true, user has site-level restrictions and should NOT see GROUP/COMPANY options
  hasSiteRestrictions: boolean;

  // Platform role: 'owner' = admin features (settings, questionnaire design), 'guest' = limited
  isPlatformOwner: boolean;

  // Per-company enabled modules — null means all enabled (default)
  enabledModules: string[] | null;
}

interface ViewContextType extends ViewState {
  // Loading states
  isLoading: boolean;
  isSaving: boolean;

  // Available options
  accessibleSites: Site[];
  accessibleCompanies: Company[];
  companyGroup: CompanyGroup | null;

  // Actions
  setViewLevel: (level: ViewLevel) => void;
  selectGroup: (groupId: number | null, groupName?: string) => void;
  selectCompany: (companyId: number | null, companyName?: string) => void;
  /** Resolves once the new site is saved, so a caller can fetch in its scope (CR-7037). */
  selectSite: (siteId: number | null, siteName?: string) => Promise<void>;
  togglePortalMode: () => void;
  setPortalMode: (mode: PortalMode) => void;

  // Reset to default view
  resetView: () => void;

  // Get query params for API calls based on current view
  getViewQueryParams: () => Record<string, number | undefined>;

  // Stable key that changes whenever the view context changes (for useEffect dependencies)
  viewKey: string;
}

const defaultViewState: ViewState = {
  viewLevel: 'COMPANY',
  selectedGroupId: null,
  selectedCompanyId: null,
  selectedSiteId: null,
  selectedGroupName: null,
  selectedCompanyName: null,
  selectedSiteName: null,
  portalMode: 'customer',
  supportsDualMode: false,
  supportsCustomerPortal: false,
  hasSiteRestrictions: false,
  isPlatformOwner: false,
  enabledModules: null,
};

const ViewContext = createContext<ViewContextType | undefined>(undefined);

// Local storage key for quick initial load (fallback cache)
const STORAGE_KEY = 'helix_view_context_cache';

/**
 * Provides hierarchical view context (Group / Company / Site) and portal mode switching.
 * Persists the selected view level to the backend so it survives across sessions and devices.
 * Resets automatically when the user changes (e.g. impersonation start/stop).
 */
export function ViewProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isImpersonating } = useAuth();

  // CR-3396: Check if user is a super admin (Helix platform admin).
  //
  // CR-5374 r3: Django is_staff / is_superuser flags are administrative
  // privileges over the /admin site, orthogonal to business-data scoping.
  // A Customer Admin (e.g. Jacq Miller) can legitimately carry is_staff to
  // manage their own company's users without being treated as a platform-
  // wide rollup admin. Mirror the backend's `get_effective_company_type`
  // rule (backend/security/access.py:23 — "'helix' is reserved for users
  // whose own company is the Helix platform tenant"): only the platform
  // field grants the synthetic "All Companies" rollup. Previously this
  // check returned true for any Django staff user, so customer admins
  // saw a synthetic rollup labelled "All Companies" in the view-data-for
  // picker instead of their actual home group (e.g. "Acme – Aerospace
  // Systems") — picking that rollup sent group=null to the backend and
  // collapsed their User Management list back to a single site.
  const isSuperAdmin = user?.platform === 'HELIX' || user?.company_type === 'helix';

  // Track previous user to detect impersonation changes
  const prevUserIdRef = useRef<number | null>(null);

  // Debounce timer for saving to DB
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [viewState, setViewState] = useState<ViewState>(defaultViewState);
  const [isLoading, setIsLoading] = useState(true);
  // Identity the loader keys on (mirrors loadViewContext's deps). A load is
  // OWED whenever this differs from the key of the last COMPLETED load, and
  // `isLoading` is reported true for that whole window — not just from the
  // moment the async loader has started. Without this there is a gap on the
  // login / fresh-token path: the unauthenticated early-return sets
  // isLoading=false, the user then arrives, and pages mount and fire their
  // first fetch (unscoped — selectedSite/Company still null) before the
  // loader's own effect runs and flips isLoading back to true. Every
  // view-scoped page then fetched twice: once without ?site=/?company= and
  // again with it.
  const loadKey = `${isAuthenticated}|${user?.company ?? ''}|${isSuperAdmin}|${isImpersonating}`;
  const loadKeyRef = useRef(loadKey);
  loadKeyRef.current = loadKey;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [accessibleSites, setAccessibleSites] = useState<Site[]>([]);
  const [accessibleCompanies, setAccessibleCompanies] = useState<Company[]>([]);
  const [companyGroup, setCompanyGroup] = useState<CompanyGroup | null>(null);

  // Flag to prevent saving during initial load
  const isInitialLoadRef = useRef(true);

  // Save view context to database immediately (no debounce).
  // Backend views read current_site/current_company from the user profile,
  // so the DB must be updated BEFORE data-fetching API calls fire.
  const saveViewContextToDb = useCallback(async (state: ViewState) => {
    if (!isAuthenticated) return;

    // Clear any pending save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    try {
      setIsSaving(true);
      // CR-4690: The synthetic "All Companies" group for super admins has
      // id=0, which doesn't correspond to a real CompanyGroup row. Sending
      // current_group=0 makes the backend FK validator reject the PATCH, so
      // the group selection silently never persists. Coerce 0 → null so the
      // backend accepts the save; on restore we reconstruct the synthetic
      // group from viewLevel='GROUP' + current_group=null + isSuperAdmin.
      const groupId = state.selectedGroupId === 0 ? null : state.selectedGroupId;
      const update: ViewContextUpdate = {
        current_view_level: state.viewLevel,
        current_group: groupId,
        current_company: state.selectedCompanyId,
        current_site: state.selectedSiteId,
        portal_mode: state.portalMode,
      };
      await coreAPI.updateViewContext(update);

      // Also update local cache for quick initial load
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        // Ignore localStorage errors
      }
    } catch (error) {
      console.error('Failed to save view context to database:', error);
    } finally {
      setIsSaving(false);
    }
  }, [isAuthenticated]);

  // CR-3396 FIX: Reset view state when user changes (impersonation start/stop)
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    const prevUserId = prevUserIdRef.current;

    // Detect user change (impersonation start/stop or different login)
    if (prevUserId !== null && currentUserId !== null && prevUserId !== currentUserId) {
      // Clear local cache
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // Ignore
      }

      // Reset to default state - will be loaded from DB
      setViewState(defaultViewState);
      isInitialLoadRef.current = true;
    }

    // Update ref for next comparison
    prevUserIdRef.current = currentUserId;
  }, [user?.id]);

  // Load view context from database and accessible entities
  const loadViewContext = useCallback(async () => {
    // Captured at call time: if the identity changes mid-flight, this load's
    // completion must not mark the NEW identity as loaded.
    const startedForKey = loadKeyRef.current;
    if (!isAuthenticated || !user?.company) {
      setAccessibleSites([]);
      setAccessibleCompanies([]);
      setCompanyGroup(null);
      setIsLoading(false);
      setLoadedKey(startedForKey);
      return;
    }

    setIsLoading(true);
    isInitialLoadRef.current = true;

    try {
      // Load view context from database
      const viewContextResponse = await coreAPI.getViewContext();
      const dbContext = viewContextResponse.data;

      // Load sites accessible to the user
      const sitesResponse = await coreAPI.getAccessibleSites();
      setAccessibleSites(sitesResponse.data || []);

      // Load the user's company details
      const companyResponse = await coreAPI.getCompany(user.company);
      const company = companyResponse.data;

      // Check if company supports dual portal mode
      // Only customer/helix companies can have dual mode; pure suppliers never switch
      const supportsDual = company.portal_mode === 'both' && company.company_type !== 'supplier';

      // Check if company supports customer portal mode (suppliers managing customers)
      const supportsCustomerPortalMode = company.company_type === 'supplier' && company.customer_portal_enabled === true;

      // Set initial portal mode based on company type
      let initialPortalMode: PortalMode = 'customer';
      if (company.company_type === 'supplier') {
        initialPortalMode = 'supplier';
      }

      // Load accessible companies
      const companiesResponse = await coreAPI.getAccessibleCompanies();
      const accessibleCompaniesList = companiesResponse.data.results || [company];
      setAccessibleCompanies(accessibleCompaniesList);

      // Load group info
      const groupResponse = await coreAPI.getAccessibleCompanyGroup();
      let userGroup = groupResponse.data;

      // CR-4690 round 2: For super admins (Helix-tenant users), the
      // conceptual primary rollup is always platform-wide "All Companies",
      // independent of whether their incidental home company sits in a
      // real CompanyGroup. Previously this synthesised the group ONLY
      // when ``getAccessibleCompanyGroup`` returned null, which broke for
      // Helix admins (e.g. sgleeson@valuechain.com) whose profile.company
      // was a customer division (Toulouse) belonging to a real group —
      // the endpoint then returned that real group, the synthetic was
      // skipped, and the TopBar's "All Companies" entry disappeared
      // (Jacq's CR-4690 follow-up). Synthesise unconditionally for
      // super admins so the rollup is always available.
      if (isSuperAdmin) {
        userGroup = {
          id: 0,
          name: 'All Companies',
          description: 'All companies in the platform',
          headquarters_country: '',
          website: '',
          level: 0,
          company_count: accessibleCompaniesList.length,
          child_group_count: 0,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as CompanyGroup;
      }
      setCompanyGroup(userGroup);

      // Set view state from database or defaults
      const hasDbContext = dbContext.current_view_level || dbContext.current_company || dbContext.current_group;

      // Security: Check if user has site-level restrictions from API
      const hasSiteRestrictions = dbContext.has_site_restrictions || false;
      // Platform role: owner gets admin features, guest gets limited view
      const isPlatformOwner = dbContext.platform_role === 'owner' || user?.is_superuser === true || user?.is_staff === true;
      const enabledModules = dbContext.enabled_modules || null;

      if (hasDbContext) {
        // Use database-stored view context
        // CR-5315: site-restricted users can still hold a GROUP view when
        // they're group-level (CR-5311 assigns every site in the group via
        // UserSitePermission). Honour a persisted GROUP level if a parent
        // group exists; otherwise fall back to SITE so explicit-site users
        // who genuinely have a narrow scope cannot escalate.
        const effectiveViewLevel = hasSiteRestrictions
          ? ((dbContext.current_view_level === 'GROUP' && userGroup) ? 'GROUP' : 'SITE')
          : (dbContext.current_view_level || 'COMPANY');

        // For site-restricted users, auto-select first accessible site if none selected
        const defaultSite = (sitesResponse.data && sitesResponse.data.length > 0) ? sitesResponse.data[0] : null;
        const effectiveSiteId = hasSiteRestrictions && !dbContext.current_site && defaultSite
          ? defaultSite.id
          : dbContext.current_site;
        const effectiveSiteName = hasSiteRestrictions && !dbContext.current_site && defaultSite
          ? defaultSite.name
          : dbContext.current_site_name;

        // CR-4690: Project the persisted context through the active view level
        // so any stale cross-level fields from earlier sessions are ignored on
        // rehydration. Without this, a customer returning from impersonating
        // a supplier could see the top-bar snap back to a site because the
        // profile still carried a current_site from before the group switch.
        const isGroup = effectiveViewLevel === 'GROUP';
        const isCompany = effectiveViewLevel === 'COMPANY';

        // CR-4690: Reconstruct synthetic "All Companies" group for super
        // admins. The save path coerces id=0 → null (see saveViewContextToDb),
        // so on restore current_group is null even though the view level is
        // GROUP. Restoring the synthetic id/name lets the TopBar render
        // "All Companies" instead of falling back to a stale company name.
        let restoredGroupId: number | null = dbContext.current_group;
        let restoredGroupName: string | null = dbContext.current_group_name;
        if (isGroup && !restoredGroupId && isSuperAdmin && userGroup) {
          restoredGroupId = userGroup.id;
          restoredGroupName = userGroup.name;
        }

        // CR-4665 rework: When an admin impersonates a supplier user, the
        // DB-persisted view context for that supplier may still point at a
        // stale scope from a previous session (e.g., another supplier they
        // were last assigned to). Override to the impersonated user's home
        // company/site so the landing page shows their own data, not
        // someone else's. Group view is cleared because a supplier user's
        // home is a company, not a group.
        // CR-4536: Only force-own-scope on a FRESH impersonation start —
        // not on every reload/navigation during an ongoing impersonation.
        // startImpersonation clears STORAGE_KEY before its hard reload, so
        // absence of the cache = fresh start (apply safety override);
        // presence = ViewContext has already hydrated once this session
        // (either via the landing override or an admin scope switch), so
        // honour the DB state the admin navigated to.
        const hasCachedViewContext = (() => {
          try {
            return !!localStorage.getItem(STORAGE_KEY);
          } catch {
            return false;
          }
        })();
        // CR-4665's original intent was to override stale supplier
        // contexts when an admin impersonates a supplier. CR-4690 round 2:
        // skip the override when the impersonated user is a super admin —
        // their saved scope (typically GROUP / synthetic "All Companies")
        // is intentional, and forcing COMPANY/home would silently demote
        // them to their incidental home company on every Login As cycle.
        const forceOwnScope = isImpersonating && !hasCachedViewContext && !isSuperAdmin;
        // CR-4858: For multi-company users, "their home" is ambiguous — the
        // user.company field is just the *primary* assignment. If the admin
        // started impersonation from a company-scoped surface (e.g. Corex's
        // Users tab) and AuthContext stashed that company in TokenManager,
        // prefer it so the landing scope matches what the admin clicked
        // through, not whichever company happens to be stamped as primary.
        const impersonationContext = forceOwnScope
          ? TokenManager.getImpersonationContext()
          : null;
        // CR-5316 rework: When Login As was invoked from a Supplier Group
        // page, AuthContext stashed the group as the access context. Land
        // the impersonated session at GROUP view-level — the previous
        // forceOwnScope path hard-pinned COMPANY, which silently dropped
        // the group context and showed site-level data instead.
        const impersonationGroupContext = forceOwnScope
          ? TokenManager.getImpersonationGroupContext()
          : null;
        const ownCompanyId = impersonationContext?.id ?? user.company ?? null;
        const ownCompanyName = impersonationContext?.name ?? company.name;
        const forceGroupScope = forceOwnScope && !!impersonationGroupContext;
        const nextState: ViewState = {
          viewLevel: forceGroupScope
            ? 'GROUP'
            : (forceOwnScope ? 'COMPANY' : effectiveViewLevel),
          // CR-5315: drop the !hasSiteRestrictions gate on the group
          // selection — group-level users (whose effectiveViewLevel above
          // can already be GROUP) need restoredGroupId to round-trip.
          selectedGroupId: forceGroupScope
            ? impersonationGroupContext.id
            : (forceOwnScope ? null : (isGroup ? restoredGroupId : null)),
          selectedGroupName: forceGroupScope
            ? impersonationGroupContext.name
            : (forceOwnScope ? null : (isGroup ? restoredGroupName : null)),
          selectedCompanyId: forceGroupScope
            ? null
            : (forceOwnScope
              ? ownCompanyId
              : (isCompany && !hasSiteRestrictions ? dbContext.current_company : null)),
          selectedCompanyName: forceGroupScope
            ? null
            : (forceOwnScope
              ? ownCompanyName
              : (isCompany && !hasSiteRestrictions ? dbContext.current_company_name : null)),
          // CR-4858 follow-up: pin selectedSite to the same id as
          // selectedCompany on a fresh impersonation. Company-IS-site in
          // this codebase, and get_accessible_po_ids on the supplier
          // branch keys off profile.current_site_id — leaving site=null
          // here PATCHes the backend reset away (current_site -> null),
          // dropping the user back to "filter by profile.company.id"
          // (the impersonated user's home, not the company the admin
          // clicked through).
          selectedSiteId: forceGroupScope
            ? null
            : (forceOwnScope ? ownCompanyId : (isGroup ? null : effectiveSiteId)),
          selectedSiteName: forceGroupScope
            ? null
            : (forceOwnScope ? ownCompanyName : (isGroup ? null : effectiveSiteName)),
          portalMode: isImpersonating ? initialPortalMode : (dbContext.portal_mode || initialPortalMode),
          supportsDualMode: supportsDual,
          supportsCustomerPortal: supportsCustomerPortalMode,
          hasSiteRestrictions,
          isPlatformOwner,
          enabledModules,
        };
        setViewState(nextState);
        // CR-4665 rework: Also persist the impersonation override to DB —
        // otherwise the backend's get_accessible_po_ids() keeps reading the
        // stale profile.current_site_id and serves the wrong supplier's
        // data even though the UI selector reads correctly.
        if (forceOwnScope) {
          saveViewContextToDb(nextState);
        }
      } else {
        // No DB context - use defaults based on user role
        // Site-restricted users can only use SITE level
        const defaultViewLevel = hasSiteRestrictions ? 'SITE' : (isSuperAdmin ? 'GROUP' : 'COMPANY');
        const defaultSite = (sitesResponse.data && sitesResponse.data.length > 0) ? sitesResponse.data[0] : null;
        setViewState({
          viewLevel: defaultViewLevel,
          selectedGroupId: hasSiteRestrictions ? null : (userGroup?.id ?? null),
          selectedGroupName: hasSiteRestrictions ? null : (userGroup?.name ?? null),
          selectedCompanyId: hasSiteRestrictions ? null : (user.company ?? null),
          selectedCompanyName: hasSiteRestrictions ? null : company.name,
          selectedSiteId: hasSiteRestrictions && defaultSite ? defaultSite.id : null,
          selectedSiteName: hasSiteRestrictions && defaultSite ? defaultSite.name : null,
          portalMode: initialPortalMode,
          supportsDualMode: supportsDual,
          supportsCustomerPortal: supportsCustomerPortalMode,
          hasSiteRestrictions,
          isPlatformOwner,
          enabledModules,
        });
      }

    } catch (error) {
      console.error('Failed to load view context:', error);
    } finally {
      setIsLoading(false);
      setLoadedKey(startedForKey);
      // Allow saving after initial load completes
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
    }
  }, [isAuthenticated, user?.company, isSuperAdmin, isImpersonating]);

  useEffect(() => {
    loadViewContext();
  }, [loadViewContext]);

  // Save to database when view state changes (after initial load).
  // Only saves non-view-scoping changes (e.g. portalMode toggled via setPortalMode).
  // View-scoping changes (site/company/group) are saved synchronously in their
  // action handlers below to avoid race conditions with data fetches.
  useEffect(() => {
    if (!isInitialLoadRef.current && isAuthenticated) {
      saveViewContextToDb(viewState);
    }
  }, [viewState, isAuthenticated, saveViewContextToDb]);

  // Helper: save view context to DB first, then update local state.
  // This ensures the backend has the correct site/company/group BEFORE
  // any data-fetching useEffects fire in consuming components.
  const saveAndSetViewState = useCallback(async (updater: (prev: ViewState) => ViewState) => {
    const newState = updater(viewState);
    // Persist to backend first so get_queryset reads the new value
    isInitialLoadRef.current = true; // suppress the useEffect save (we do it here)
    try {
      await saveViewContextToDb(newState);
    } finally {
      isInitialLoadRef.current = false;
    }
    setViewState(newState);
    // Per-customer settings (delivery grain gating in MenuSettingsContext)
    // are resolved server-side from the persisted scope — tell listeners the
    // scope moved so they re-fetch. MenuSettingsProvider sits inside this
    // provider, so a window event is the dependency-free channel.
    try {
      window.dispatchEvent(new CustomEvent('helix:view-scope-changed', { detail: newState }));
    } catch { /* non-browser env */ }
  }, [viewState, saveViewContextToDb]);

  // Actions
  // CR-4690: All view-level selectors clear the fields that don't belong to
  // the new level, so the persisted profile context is internally consistent
  // (e.g. picking "Group View" also nulls out current_site / current_company
  // in the DB). Without this, a stale site lingers in the user profile and
  // re-inflates after impersonation ends, replacing the group-level view.
  const setViewLevel = useCallback((level: ViewLevel) => {
    saveAndSetViewState(prev => {
      if (level === 'GROUP') {
        // CR-4536: When switching to GROUP, auto-select the companyGroup if no group is
        // currently selected. Without this, switching from SITE→GROUP left the group
        // fields null, causing the TopBar to show a stale company/site name.
        const groupId = prev.selectedGroupId ?? companyGroup?.id ?? null;
        const groupName = prev.selectedGroupName ?? companyGroup?.name ?? null;
        return {
          ...prev,
          viewLevel: level,
          selectedGroupId: groupId,
          selectedGroupName: groupName,
          selectedCompanyId: null,
          selectedCompanyName: null,
          selectedSiteId: null,
          selectedSiteName: null,
        };
      }
      if (level === 'COMPANY') {
        return {
          ...prev,
          viewLevel: level,
          selectedGroupId: null,
          selectedGroupName: null,
        };
      }
      // SITE level — ensure a site is selected if available
      if (level === 'SITE' && !prev.selectedSiteId && accessibleSites.length > 0) {
        return {
          ...prev,
          viewLevel: level,
          selectedGroupId: null,
          selectedGroupName: null,
          selectedSiteId: accessibleSites[0].id,
          selectedSiteName: accessibleSites[0].name,
        };
      }
      return {
        ...prev,
        viewLevel: level,
        selectedGroupId: null,
        selectedGroupName: null,
      };
    });
  }, [accessibleSites, companyGroup, saveAndSetViewState]);

  const selectGroup = useCallback((groupId: number | null, groupName?: string) => {
    saveAndSetViewState(prev => ({
      ...prev,
      selectedGroupId: groupId,
      selectedGroupName: groupName || null,
      selectedCompanyId: null,
      selectedCompanyName: null,
      selectedSiteId: null,
      selectedSiteName: null,
      viewLevel: groupId !== null ? 'GROUP' : prev.viewLevel,
    }));
  }, [saveAndSetViewState]);

  const selectCompany = useCallback((companyId: number | null, companyName?: string) => {
    saveAndSetViewState(prev => ({
      ...prev,
      selectedGroupId: null,
      selectedGroupName: null,
      selectedCompanyId: companyId,
      selectedCompanyName: companyName || null,
      viewLevel: companyId ? 'COMPANY' : prev.viewLevel,
      // Company IS Site — set site to same company for preferences/currency
      selectedSiteId: companyId,
      selectedSiteName: companyName || null,
    }));
  }, [saveAndSetViewState]);

  const selectSite = useCallback((siteId: number | null, siteName?: string) => {
    return saveAndSetViewState(prev => ({
      ...prev,
      selectedGroupId: null,
      selectedGroupName: null,
      selectedSiteId: siteId,
      selectedSiteName: siteName || null,
      viewLevel: siteId ? 'SITE' : prev.viewLevel,
    }));
  }, [saveAndSetViewState]);

  const togglePortalMode = useCallback(async () => {
    // Build list of available modes based on company capabilities
    const availableModes: PortalMode[] = [];

    // Base modes depend on dual mode support
    if (viewState.supportsDualMode) {
      availableModes.push('customer', 'supplier');
    } else {
      // If no dual mode, use current mode as the only base option
      availableModes.push(viewState.portalMode === 'customer_manager' ? 'supplier' : viewState.portalMode);
    }

    // Add customer_manager mode if supported (for suppliers managing customers)
    if (viewState.supportsCustomerPortal) {
      availableModes.push('customer_manager');
    }

    // If only one mode available, don't toggle
    if (availableModes.length <= 1) return;

    const currentIndex = availableModes.indexOf(viewState.portalMode);
    const nextIndex = (currentIndex + 1) % availableModes.length;
    const newMode = availableModes[nextIndex];

    // Save to backend BEFORE reloading - don't rely on debounced save
    try {
      await coreAPI.updateViewContext({
        current_view_level: viewState.viewLevel,
        current_group: viewState.selectedGroupId,
        current_company: viewState.selectedCompanyId,
        current_site: viewState.selectedSiteId,
        portal_mode: newMode,
      });
      // Also update localStorage for quick initial load
      const newState = { ...viewState, portalMode: newMode };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    } catch (error) {
      console.error('Failed to save portal mode:', error);
    }

    // Force a full page reload to ensure all data is refetched for the new mode
    window.location.href = '/';
  }, [viewState]);

  const setPortalMode = useCallback((mode: PortalMode) => {
    // Check if the requested mode is allowed
    const canSetMode =
      mode === viewState.portalMode ||  // Same mode is always allowed
      (mode === 'customer_manager' && viewState.supportsCustomerPortal) ||  // customer_manager requires support
      (mode !== 'customer_manager' && viewState.supportsDualMode);  // Other modes require dual mode

    if (!canSetMode) return;

    setViewState(prev => ({
      ...prev,
      portalMode: mode,
    }));
  }, [viewState.supportsDualMode, viewState.supportsCustomerPortal, viewState.portalMode]);

  const resetView = useCallback(() => {
    setViewState({
      ...defaultViewState,
      selectedCompanyId: user?.company || null,
      portalMode: user?.company_type === 'supplier' ? 'supplier' : 'customer',
    });
  }, [user?.company, user?.company_type]);

  // Get query params for API calls based on current view
  const getViewQueryParams = useCallback((): Record<string, number | undefined> => {
    const params: Record<string, number | undefined> = {};

    switch (viewState.viewLevel) {
      case 'GROUP':
        if (viewState.selectedGroupId) {
          params['group'] = viewState.selectedGroupId;
        }
        break;
      case 'COMPANY':
        if (viewState.selectedCompanyId) {
          params['company'] = viewState.selectedCompanyId;
        }
        break;
      case 'SITE':
        if (viewState.selectedSiteId) {
          params['site'] = viewState.selectedSiteId;
        }
        break;
    }

    return params;
  }, [viewState.viewLevel, viewState.selectedGroupId, viewState.selectedCompanyId, viewState.selectedSiteId]);

  // Stable key that changes whenever view-scoping params change.
  // Pages can depend on this single value instead of 4 individual IDs.
  const viewKey = useMemo(
    () => `${viewState.viewLevel}-${viewState.selectedGroupId}-${viewState.selectedCompanyId}-${viewState.selectedSiteId}`,
    [viewState.viewLevel, viewState.selectedGroupId, viewState.selectedCompanyId, viewState.selectedSiteId]
  );

  const value: ViewContextType = {
    ...viewState,
    // Loading until the load for the CURRENT identity has completed (see loadKey).
    isLoading: isLoading || loadedKey !== loadKey,
    isSaving,
    accessibleSites,
    accessibleCompanies,
    companyGroup,
    setViewLevel,
    selectGroup,
    selectCompany,
    selectSite,
    togglePortalMode,
    setPortalMode,
    resetView,
    getViewQueryParams,
    viewKey,
  };

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
}

/**
 * Accesses the current view context including view level, selected entities, and portal mode.
 * Must be used within a {@link ViewProvider}.
 *
 * @returns View state with entity selectors, portal mode toggle, and API query param helpers
 */
export function useView() {
  const context = useContext(ViewContext);
  if (context === undefined) {
    throw new Error('useView must be used within a ViewProvider');
  }
  return context;
}

/**
 * The current view-context key, or '' when no ViewProvider is mounted.
 *
 * For effects that only need to re-run when the site switcher changes scope —
 * filter-option loaders whose options come from the sites in view — without
 * making the component unusable outside the provider.
 *
 * @example
 *   const viewKey = useViewKey();
 *   useEffect(() => { loadSupplierOptions(); }, [viewKey]);
 * @returns The view key, or an empty string outside a ViewProvider
 */
export function useViewKey(): string {
  return useContext(ViewContext)?.viewKey ?? '';
}

/**
 * The group the switcher is pointed at, or null when it is not on a group.
 *
 * Companion to {@link useViewKey} and safe outside a ViewProvider for the same
 * reason. For screens that configure something which can itself live at group
 * level — supplier performance settings, where "Group Default" is a real,
 * editable scope — so that pointing the app at a group and configuring that
 * group are the same gesture rather than two.
 *
 * Returns null at COMPANY or SITE level even when a group is selected
 * underneath, because at those levels the user is looking at one company and
 * the group is only its ancestor.
 *
 * @example
 *   const group = useViewGroup();
 *   if (group) setScope({ scope: 'group', id: group.id });
 * @returns `{ id, name }` for the group in view, or null
 */
export function useViewGroup(): { id: number; name: string | null } | null {
  const context = useContext(ViewContext);
  // 0 is the superadmin "all groups" sentinel (see the persist call above), not
  // a real group id, so it is not something that can be configured.
  const id = context?.viewLevel === 'GROUP' ? context.selectedGroupId : null;
  const name = context?.selectedGroupName ?? null;
  // Memoised so the identity is stable across renders: callers put this
  // straight into effect dependencies, and a fresh object each render would
  // re-run them forever.
  return useMemo(() => (id ? { id, name } : null), [id, name]);
}

export default ViewContext;
